import * as React from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, FormControlLabel, Paper, Stack, Switch, Tab, Tabs,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {BomPrinting as Api} from '../../controllers';
import {TokenValidator} from '../../utils';
import {AUTO_SCAN_IDLE_MS, advanceScannerState, emptyScannerState, looksLikeCompletedScan} from './scannerDetection';

const ADMIN_ROLE = 4;
const emptyPrinter = {name: '', model: '', ip: '', port: 9100, equipmentId: '', active: true, setDefault: false};
const displayTime = value => value ? new Date(value).toLocaleString() : '';
const newKey = () => window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, value => {
        const random = Math.random() * 16 | 0;
        return (value === 'x' ? random : ((random & 0x3) | 0x8)).toString(16);
    });

export default function Main() {
    const scannerRef = React.useRef(null);
    const autoPrintTimerRef = React.useRef(null);
    const printInFlightRef = React.useRef(false);
    const scannerStateRef = React.useRef(emptyScannerState());
    const [tab, setTab] = React.useState(0);
    const [orderId, setOrderId] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState(null);
    const [assignments, setAssignments] = React.useState([]);
    const [history, setHistory] = React.useState([]);
    const [printers, setPrinters] = React.useState([]);
    const [adminPrinters, setAdminPrinters] = React.useState([]);
    const [isAdmin, setIsAdmin] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [form, setForm] = React.useState({fgsCode: '', copies: 1, reason: ''});
    const [prn, setPrn] = React.useState(null);
    const [nlbl, setNlbl] = React.useState(null);
    const [printerForm, setPrinterForm] = React.useState(emptyPrinter);
    const [editingPrinterId, setEditingPrinterId] = React.useState(null);
    const [printerBusy, setPrinterBusy] = React.useState(false);

    const getToken = React.useCallback(() => TokenValidator.GetStaffUser(), []);
    const focusScanner = React.useCallback(() => setTimeout(() => scannerRef.current && scannerRef.current.focus(), 50), []);
    const clearAutoPrintTimer = React.useCallback(() => {
        if (autoPrintTimerRef.current) clearTimeout(autoPrintTimerRef.current);
        autoPrintTimerRef.current = null;
    }, []);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError('');
        const token = await getToken();
        const decoded = await TokenValidator.SimplifyToken(token);
        const adminAccess = (Number(decoded.roles) & ADMIN_ROLE) === ADMIN_ROLE;
        setIsAdmin(adminAccess);
        const [printerResponse, assignmentResponse, historyResponse] = await Promise.all([
            Api.Printers(token), Api.Assignments(token), Api.History(token, 50)
        ]);
        setLoading(false);
        if (!printerResponse || !printerResponse.success) return setError(printerResponse ? printerResponse.message : 'Unable to load printer.');
        if (!assignmentResponse || !assignmentResponse.success) return setError(assignmentResponse ? assignmentResponse.message : 'Unable to load BOM assignments.');
        if (!historyResponse || !historyResponse.success) return setError(historyResponse ? historyResponse.message : 'Unable to load print history.');
        if (adminAccess) {
            const adminResponse = await Api.AdminPrinters(token);
            if (!adminResponse || !adminResponse.success) return setError(adminResponse ? adminResponse.message : 'Unable to load printer options.');
            setAdminPrinters(adminResponse.result || []);
        }
        setPrinters(printerResponse.result || []);
        setAssignments(assignmentResponse.result || []);
        setHistory(historyResponse.result || []);
        focusScanner();
    }, [focusScanner, getToken]);

    React.useEffect(() => { load(); }, [load]);
    React.useEffect(() => { if (tab === 0) focusScanner(); }, [tab, focusScanner]);
    React.useEffect(() => () => clearAutoPrintTimer(), [clearAutoPrintTimer]);

    const submitOrder = React.useCallback(async rawValue => {
        const value = String(rawValue || '').trim();
        if (!value || printInFlightRef.current) return;
        printInFlightRef.current = true;
        clearAutoPrintTimer();
        setBusy(true);
        setError('');
        setSuccess(null);
        try {
            const response = await Api.Print(await getToken(), value, newKey());
            if (!response || !response.success) {
                setError(response ? `${response.code ? `${response.code}: ` : ''}${response.message}` : 'Unable to print BOM label.');
                return;
            }
            setSuccess(response.result);
            setOrderId('');
            scannerStateRef.current = emptyScannerState();
            const recent = await Api.History(await getToken(), 50);
            if (recent && recent.success) setHistory(recent.result || []);
        } catch (printError) {
            setError(printError && printError.message ? printError.message : 'Unable to print BOM label.');
        } finally {
            printInFlightRef.current = false;
            setBusy(false);
            focusScanner();
        }
    }, [clearAutoPrintTimer, focusScanner, getToken]);

    const print = event => {
        event.preventDefault();
        submitOrder(orderId);
    };

    const scannerChanged = event => {
        const value = event.target.value.replace(/[\r\n]/g, '');
        const now = window.performance ? window.performance.now() : Date.now();
        const state = advanceScannerState(scannerStateRef.current, value, now);
        scannerStateRef.current = state;
        setOrderId(value);
        clearAutoPrintTimer();

        if (looksLikeCompletedScan(state)) {
            autoPrintTimerRef.current = setTimeout(() => {
                if (scannerStateRef.current.value === value && !printInFlightRef.current) submitOrder(value);
            }, AUTO_SCAN_IDLE_MS);
        }
    };

    const upload = async event => {
        event.preventDefault();
        if (!prn || uploading) return setError('Select a PRN template.');
        const data = new FormData();
        data.append('fgsCode', form.fgsCode);
        if (!printers.length) return setError('No active BOM printer is configured.');
        data.append('deviceId', String(printers[0].id));
        data.append('copies', String(form.copies));
        data.append('reason', form.reason);
        data.append('prn', prn);
        if (nlbl) data.append('nlbl', nlbl);
        setUploading(true);
        setError('');
        const response = await Api.Upload(await getToken(), data);
        setUploading(false);
        if (!response || !response.success) return setError(response ? response.message : 'Unable to upload template.');
        setForm({fgsCode: '', copies: 1, reason: ''});
        setPrn(null);
        setNlbl(null);
        setSuccess({upload: true, ...response.result});
        await load();
        setTab(1);
    };

    const printerPayload = (row, overrides = {}) => ({
        name: row.name,
        model: row.model || '',
        ip: row.ip,
        port: Number(row.port),
        equipmentId: row.equipment_id || row.equipmentId || '',
        active: row.active,
        setDefault: false,
        ...overrides
    });

    const savePrinter = async event => {
        event.preventDefault();
        if (printerBusy) return;
        setPrinterBusy(true); setError(''); setSuccess(null);
        const token = await getToken();
        const payload = {...printerForm, port: Number(printerForm.port)};
        const response = editingPrinterId
            ? await Api.UpdatePrinter(token, editingPrinterId, payload)
            : await Api.CreatePrinter(token, payload);
        setPrinterBusy(false);
        if (!response || !response.success) return setError(response ? response.message : 'Unable to save printer.');
        setPrinterForm(emptyPrinter); setEditingPrinterId(null);
        setSuccess({message: editingPrinterId ? 'Printer updated.' : 'Printer added.'});
        await load(); setTab(4);
    };

    const editPrinter = row => {
        setEditingPrinterId(row.id);
        setPrinterForm(printerPayload(row, {setDefault: row.is_default}));
    };

    const changePrinterState = async row => {
        if (row.active && !window.confirm(`Deactivate ${row.name}? Print history will be preserved.`)) return;
        setPrinterBusy(true); setError(''); setSuccess(null);
        const response = await Api.UpdatePrinter(await getToken(), row.id, printerPayload(row, {active: !row.active}));
        setPrinterBusy(false);
        if (!response || !response.success) return setError(response ? response.message : 'Unable to change printer status.');
        setSuccess({message: row.active ? 'Printer deactivated; history was preserved.' : 'Printer reactivated.'});
        await load(); setTab(4);
    };

    const makeDefault = async row => {
        setPrinterBusy(true); setError(''); setSuccess(null);
        const response = await Api.UpdatePrinter(await getToken(), row.id, printerPayload(row, {active: true, setDefault: true}));
        setPrinterBusy(false);
        if (!response || !response.success) return setError(response ? response.message : 'Unable to select the default printer.');
        setSuccess({message: `${row.name} is now the default BOM printer.`});
        await load(); setTab(4);
    };

    const testPrinter = async row => {
        setPrinterBusy(true); setError(''); setSuccess(null);
        const response = await Api.TestPrinter(await getToken(), row.id);
        setPrinterBusy(false);
        if (!response || !response.success) return setError(response ? response.message : 'Printer connection test failed.');
        setSuccess({message: `${row.name}: connection succeeded in ${response.result.latencyMs} ms. No label data was sent.`});
    };

    return (
        <Box sx={{p: {xs: 2, md: 3}}}>
            <Stack direction={{xs: 'column', md: 'row'}} justifyContent='space-between' gap={2} sx={{mb: 2}}>
                <Box>
                    <Typography variant='h4'>BOM Automatic Printing</Typography>
                    <Typography color='text.secondary'>Scan an order and print the active BOM label for its FGS.</Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Stack>

            {error && <Alert severity='error' sx={{mb: 2}} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity='success' sx={{mb: 2}} onClose={() => setSuccess(null)}>
                {success.message ? success.message : success.upload
                    ? `${success.fgsCode} template version ${success.version} is active.`
                    : `PRINTED — Order ${success.orderNumber}, Case ${success.caseId || 'N/A'}, ${success.fgs}, ${success.copies} copy/copies on ${success.printer}.`}
            </Alert>}

            <Paper variant='outlined'>
                <Tabs value={tab} onChange={(_, value) => setTab(value)} variant='scrollable' scrollButtons='auto'>
                    <Tab label='Scan & Print' />
                    <Tab label='FGS Templates' />
                    <Tab label='Print History' />
                    {isAdmin && <Tab label='Upload Template' />}
                    {isAdmin && <Tab label='Printer Options' />}
                </Tabs>
                <Divider />

                {loading ? <Box sx={{p: 5, textAlign: 'center'}}><CircularProgress /></Box> : <>
                    {tab === 0 && <Box component='form' onSubmit={print} sx={{p: {xs: 2, md: 4}, maxWidth: 900, mx: 'auto'}}>
                        <Typography variant='h6' gutterBottom>Order scanner</Typography>
                        <Alert severity={printers.length ? 'info' : 'warning'} sx={{mb: 2}}>
                            Default network printer: {printers.length ? `${printers[0].name} — ${printers[0].ip}:${printers[0].port}` : 'Unavailable'}.
                            USB configuration is intentionally deferred.
                        </Alert>
                        <Stack direction={{xs: 'column', md: 'row'}} gap={2}>
                            <TextField inputRef={scannerRef} fullWidth autoComplete='off' label='Order ID'
                                placeholder='Scan to print automatically, or type and press Enter' value={orderId}
                                onChange={scannerChanged}
                                disabled={busy || !printers.length} inputProps={{maxLength: 128}} />
                            <Button type='submit' variant='contained' size='large' startIcon={<PrintIcon />}
                                disabled={busy || !orderId.trim() || !printers.length} sx={{minWidth: 180}}>
                                {busy ? <CircularProgress size={24} color='inherit' /> : 'Print BOM'}
                            </Button>
                        </Stack>
                        <Typography variant='body2' color='text.secondary' sx={{mt: 2}}>
                            A rapid scanner input prints automatically without Enter. Manual entry still uses Enter or Print BOM.
                            The MES obtains Case ID and FGS from the order.
                        </Typography>
                    </Box>}

                    {tab === 1 && <TableContainer sx={{maxHeight: 560}}><Table stickyHeader size='small'>
                        <TableHead><TableRow><TableCell>FGS</TableCell><TableCell>Template</TableCell><TableCell>Version</TableCell><TableCell>Printer</TableCell><TableCell>Copies</TableCell><TableCell>Status</TableCell><TableCell>Updated</TableCell></TableRow></TableHead>
                        <TableBody>{assignments.map(row => <TableRow key={row.id}>
                            <TableCell>{row.fgs_code}</TableCell><TableCell>{row.prn_filename}</TableCell><TableCell>{row.version}</TableCell>
                            <TableCell>{row.effective_printer || row.printer}</TableCell><TableCell>{row.copies}</TableCell>
                            <TableCell><Chip size='small' color={row.active ? 'success' : 'default'} label={row.active ? 'Active' : 'Inactive'} /></TableCell>
                            <TableCell>{displayTime(row.updated_at)}</TableCell>
                        </TableRow>)}</TableBody>
                    </Table></TableContainer>}

                    {tab === 2 && <TableContainer sx={{maxHeight: 560}}><Table stickyHeader size='small'>
                        <TableHead><TableRow><TableCell>Date/Time</TableCell><TableCell>Order</TableCell><TableCell>Case</TableCell><TableCell>Source</TableCell><TableCell>FGS</TableCell><TableCell>Printer</TableCell><TableCell>Copies</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                        <TableBody>{history.map(row => <TableRow key={row.id}>
                            <TableCell>{displayTime(row.requested_at)}</TableCell><TableCell>{row.order_number_snapshot}</TableCell>
                            <TableCell>{row.case_id_snapshot || ''}</TableCell><TableCell>{row.source}</TableCell><TableCell>{row.fgs_code_snapshot}</TableCell>
                            <TableCell>{row.printer}</TableCell><TableCell>{row.copies}</TableCell>
                            <TableCell><Chip size='small' color={row.status === 'PRINTED' ? 'success' : row.status === 'FAILED' ? 'error' : 'warning'} label={row.status} /></TableCell>
                        </TableRow>)}</TableBody>
                    </Table></TableContainer>}

                    {isAdmin && tab === 3 && <Box component='form' onSubmit={upload} sx={{p: {xs: 2, md: 4}, maxWidth: 760}}>
                        <Typography variant='h6'>Upload a new version</Typography>
                        <Typography color='text.secondary' sx={{mb: 2}}>The previous version remains in history. The new version becomes active for the entered FGS.</Typography>
                        <Stack gap={2}>
                            <TextField required label='FGS code' placeholder='FGS-0002' value={form.fgsCode}
                                onChange={event => setForm({...form, fgsCode: event.target.value})} inputProps={{maxLength: 8}} />
                            <TextField required type='number' label='Copies' value={form.copies}
                                onChange={event => setForm({...form, copies: event.target.value})} inputProps={{min: 1, max: 20}} />
                            <TextField required multiline minRows={2} label='Reason' value={form.reason}
                                onChange={event => setForm({...form, reason: event.target.value})} inputProps={{maxLength: 500}} />
                            <Button component='label' variant='outlined' startIcon={<UploadFileIcon />}>
                                {prn ? prn.name : 'Select PRN template (required)'}
                                <input hidden type='file' accept='.prn' onChange={event => setPrn(event.target.files[0] || null)} />
                            </Button>
                            <Button component='label' variant='outlined'>
                                {nlbl ? nlbl.name : 'Select NLBL source (optional)'}
                                <input hidden type='file' accept='.nlbl' onChange={event => setNlbl(event.target.files[0] || null)} />
                            </Button>
                            <TextField label='Default printer' value={printers.length ? printers[0].name : 'Unavailable'} disabled />
                            <Button type='submit' variant='contained' disabled={uploading || !prn || !form.fgsCode.trim() || !form.reason.trim()}>
                                {uploading ? <CircularProgress size={24} color='inherit' /> : 'Save new version'}
                            </Button>
                        </Stack>
                    </Box>}

                    {isAdmin && tab === 4 && <Box sx={{p: {xs: 2, md: 3}}}>
                        <Typography variant='h6'>BOM network printers</Typography>
                        <Typography color='text.secondary' sx={{mb: 2}}>
                            Adding or testing a printer does not print a label. Deactivated printers remain in print history.
                        </Typography>
                        <Box component='form' onSubmit={savePrinter} sx={{mb: 3}}>
                            <Stack direction={{xs: 'column', lg: 'row'}} gap={2} alignItems={{lg: 'center'}}>
                                <TextField required label='Display name' value={printerForm.name}
                                    onChange={event => setPrinterForm({...printerForm, name: event.target.value})} inputProps={{maxLength: 160}} />
                                <TextField label='Model' value={printerForm.model}
                                    onChange={event => setPrinterForm({...printerForm, model: event.target.value})} inputProps={{maxLength: 80}} />
                                <TextField required label='IPv4 address' placeholder='192.168.101.58' value={printerForm.ip}
                                    onChange={event => setPrinterForm({...printerForm, ip: event.target.value})} />
                                <TextField required type='number' label='Port' value={printerForm.port}
                                    onChange={event => setPrinterForm({...printerForm, port: event.target.value})} inputProps={{min: 1, max: 65535}} sx={{maxWidth: 130}} />
                                <TextField label='Equipment ID' placeholder='EQ-0000-00' value={printerForm.equipmentId}
                                    onChange={event => setPrinterForm({...printerForm, equipmentId: event.target.value.toUpperCase()})} inputProps={{maxLength: 10}} />
                            </Stack>
                            <Stack direction={{xs: 'column', sm: 'row'}} gap={2} alignItems={{sm: 'center'}} sx={{mt: 2}}>
                                <FormControlLabel control={<Switch checked={printerForm.active}
                                    onChange={event => setPrinterForm({...printerForm, active: event.target.checked,
                                        setDefault: event.target.checked ? printerForm.setDefault : false})} />} label='Active' />
                                <FormControlLabel control={<Switch checked={printerForm.setDefault}
                                    onChange={event => setPrinterForm({...printerForm, setDefault: event.target.checked})}
                                    disabled={!printerForm.active} />} label='Set as default' />
                                <Button type='submit' variant='contained' disabled={printerBusy}>
                                    {editingPrinterId ? 'Save changes' : 'Add printer'}
                                </Button>
                                {editingPrinterId && <Button onClick={() => {setEditingPrinterId(null); setPrinterForm(emptyPrinter);}}>Cancel edit</Button>}
                            </Stack>
                        </Box>
                        <TableContainer sx={{maxHeight: 520}}><Table stickyHeader size='small'>
                            <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Model</TableCell><TableCell>IP / Port</TableCell><TableCell>Equipment</TableCell><TableCell>Status</TableCell><TableCell>Default</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
                            <TableBody>{adminPrinters.map(row => <TableRow key={row.id}>
                                <TableCell>{row.name}</TableCell><TableCell>{row.model || ''}</TableCell>
                                <TableCell>{row.ip}:{row.port}</TableCell><TableCell>{row.equipment_id || ''}</TableCell>
                                <TableCell><Chip size='small' color={row.active ? 'success' : 'default'} label={row.active ? 'Active' : 'Inactive'} /></TableCell>
                                <TableCell><Chip size='small' color={row.is_default ? 'primary' : 'default'} label={row.is_default ? 'Default' : '—'} /></TableCell>
                                <TableCell><Stack direction='row' gap={1} flexWrap='wrap'>
                                    <Button size='small' onClick={() => editPrinter(row)} disabled={printerBusy}>Edit</Button>
                                    <Button size='small' onClick={() => testPrinter(row)} disabled={printerBusy}>Test connection</Button>
                                    <Button size='small' onClick={() => makeDefault(row)} disabled={printerBusy || !row.active || row.is_default}>Set default</Button>
                                    <Button size='small' color={row.active ? 'error' : 'success'} onClick={() => changePrinterState(row)}
                                        disabled={printerBusy || (row.active && row.is_default)}>{row.active ? 'Deactivate' : 'Reactivate'}</Button>
                                </Stack></TableCell>
                            </TableRow>)}</TableBody>
                        </Table></TableContainer>
                    </Box>}
                </>}
            </Paper>
        </Box>
    );
}
